// src/components/events/BookingModal.jsx
import React, { useState } from 'react';
import { X, CreditCard, User, Mail, Phone, Users, MessageSquare } from 'lucide-react';
import { eventsService } from '../../services/eventsService';

export const BookingModal = ({ book, bookId, eventSettings, isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    attendeeCount: 1,
    attendeeNames: [''],
    specialRequests: '',
    dietaryRestrictions: '',
    agreeToTerms: false
  });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleAttendeeCountChange = (count) => {
    const newAttendeeNames = [...bookingData.attendeeNames];
    if (count > newAttendeeNames.length) {
      // Add empty names for new attendees
      while (newAttendeeNames.length < count) {
        newAttendeeNames.push('');
      }
    } else {
      // Remove excess names
      newAttendeeNames.splice(count);
    }
    
    setBookingData({
      ...bookingData,
      attendeeCount: count,
      attendeeNames: newAttendeeNames
    });
  };

  const handleAttendeeNameChange = (index, name) => {
    const newAttendeeNames = [...bookingData.attendeeNames];
    newAttendeeNames[index] = name;
    setBookingData({
      ...bookingData,
      attendeeNames: newAttendeeNames
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');

    try {
      const booking = {
        ...bookingData,
        eventId: book.id,
        eventName: book.botName,
        amount: eventSettings.requirePayment ? eventSettings.eventPrice * bookingData.attendeeCount : 0,
        paymentRequired: eventSettings.requirePayment,
        bookingDate: new Date().toISOString(),
        status: eventSettings.requirePayment ? 'pending_payment' : 'confirmed'
      };

      await eventsService.createBooking(bookId, booking);
      onSuccess();
    } catch (error) {
      console.error('Booking error:', error);
      setError('Failed to create booking. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  const totalAmount = eventSettings.requirePayment ? eventSettings.eventPrice * bookingData.attendeeCount : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">
            {eventSettings.requirePayment ? 'Book & Pay' : 'Reserve Your Spot'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Step 1: Contact Information */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={bookingData.name}
                    onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={bookingData.email}
                    onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Attendees
                </label>
                <select
                  value={bookingData.attendeeCount}
                  onChange={(e) => handleAttendeeCountChange(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {[...Array(10)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} {i === 0 ? 'person' : 'people'}
                    </option>
                  ))}
                </select>
              </div>

              {bookingData.attendeeCount > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attendee Names
                  </label>
                  <div className="space-y-2">
                    {bookingData.attendeeNames.map((name, index) => (
                      <input
                        key={index}
                        type="text"
                        value={name}
                        onChange={(e) => handleAttendeeNameChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder={`Attendee ${index + 1} name${index === 0 ? ' (you)' : ''}`}
                        required={index === 0}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700"
                >
                  Next: Additional Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Additional Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Additional Details
                </h3>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  ← Back
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requests
                </label>
                <textarea
                  value={bookingData.specialRequests}
                  onChange={(e) => setBookingData({...bookingData, specialRequests: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Any special accommodations or requests..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dietary Restrictions
                </label>
                <input
                  type="text"
                  value={bookingData.dietaryRestrictions}
                  onChange={(e) => setBookingData({...bookingData, dietaryRestrictions: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Vegetarian, vegan, allergies, etc."
                />
              </div>

              {/* Booking Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-3">Booking Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Event:</span>
                    <span className="font-medium">{book.botName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Attendees:</span>
                    <span className="font-medium">{bookingData.attendeeCount}</span>
                  </div>
                  {eventSettings.eventDate && (
                    <div className="flex justify-between">
                      <span>Date:</span>
                      <span className="font-medium">
                        {new Date(eventSettings.eventDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium text-lg pt-2 border-t">
                    <span>Total:</span>
                    <span className="text-emerald-600">
                      {eventSettings.requirePayment ? `$${totalAmount.toFixed(2)}` : 'FREE'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={bookingData.agreeToTerms}
                  onChange={(e) => setBookingData({...bookingData, agreeToTerms: e.target.checked})}
                  className="mt-1"
                  required
                />
                <label htmlFor="agreeToTerms" className="text-sm text-gray-600">
                  I agree to the event terms and conditions. I understand that 
                  {eventSettings.requirePayment ? 
                    ' payment is required to confirm my booking and refunds are subject to the event policy.' :
                    ' this reservation is free and I can cancel anytime before the event.'
                  }
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={processing || !bookingData.agreeToTerms}
                className="w-full bg-emerald-600 text-white py-3 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    {eventSettings.requirePayment ? `Pay $${totalAmount.toFixed(2)}` : 'Confirm Reservation'}
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};